import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { apiClient } from '../utils/apiClient'
import { ACTIVE_ORG_CHANGED_EVENT } from '../utils/activeOrganization'
import { Project } from '../types'

interface ProjectContextType {
  // Legacy localStorage fields (project selection persisted client-side)
  selectedProjectId: string | null
  defaultProjectId: string | null
  setSelectedProjectId: (projectId: string | null) => void
  setDefaultProjectId: (projectId: string | null) => void
  // Multi-repo Projects list
  projects: Project[]
  projectsLoading: boolean
  projectsError: string | null
  hasFetched: boolean
  refetchProjects: (options?: { silent?: boolean }) => Promise<void>
  removeProjectFromList: (projectId: number) => void
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

export const useProject = () => {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}

export const useProjects = () => {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider')
  }
  const { projects, projectsLoading, projectsError, hasFetched, refetchProjects, removeProjectFromList } = context
  return { projects, projectsLoading, projectsError, hasFetched, refetchProjects, removeProjectFromList }
}

interface ProjectProviderProps {
  children: ReactNode
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [selectedProjectId, setSelectedProjectIdState] = useState<string | null>(() => {
    return localStorage.getItem('selectedProjectId')
  })

  const [defaultProjectId, setDefaultProjectIdState] = useState<string | null>(() => {
    return localStorage.getItem('defaultProjectId')
  })

  const [projects, setProjects] = useState<Project[]>([])
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [projectsError, setProjectsError] = useState<string | null>(null)
  const [hasFetched, setHasFetched] = useState(false)
  const { user } = useAuth()

  const setSelectedProjectId = (projectId: string | null) => {
    setSelectedProjectIdState(projectId)
    if (projectId) {
      localStorage.setItem('selectedProjectId', projectId)
    } else {
      localStorage.removeItem('selectedProjectId')
    }
  }

  const setDefaultProjectId = (projectId: string | null) => {
    setDefaultProjectIdState(projectId)
    if (projectId) {
      localStorage.setItem('defaultProjectId', projectId)
    } else {
      localStorage.removeItem('defaultProjectId')
    }
  }

  const fetchProjects = async (force = false, silent = false) => {
    if (!user || (hasFetched && !force)) return
    try {
      if (!silent) {
        setProjectsLoading(true)
      }
      setProjectsError(null)
      const data = await apiClient.get('/api/projects')
      setProjects(Array.isArray(data) ? data : [])
    } catch {
      if (!silent) {
        setProjects([])
      }
      setProjectsError(null) // Silently fail — backend not yet implemented
    } finally {
      setHasFetched(true)
      if (!silent) {
        setProjectsLoading(false)
      }
    }
  }

  const refetchProjects = async (options?: { silent?: boolean }) => {
    await fetchProjects(true, options?.silent ?? false)
  }

  const removeProjectFromList = (projectId: number) => {
    setProjects((prev) => prev.filter((p) => p.id !== projectId))
    if (selectedProjectId === String(projectId)) {
      setSelectedProjectId(null)
    }
    if (defaultProjectId === String(projectId)) {
      setDefaultProjectId(null)
    }
  }

  useEffect(() => {
    if (user && !hasFetched) {
      fetchProjects()
    } else if (!user) {
      setProjects([])
      setHasFetched(false)
      setProjectsError(null)
    }
  }, [user, hasFetched])

  useEffect(() => {
    if (!user) return
    const onOrgChanged = () => {
      setSelectedProjectId(null)
      setDefaultProjectId(null)
      setProjects([])
      setHasFetched(false)
      void fetchProjects(true)
    }
    window.addEventListener(ACTIVE_ORG_CHANGED_EVENT, onOrgChanged)
    return () => window.removeEventListener(ACTIVE_ORG_CHANGED_EVENT, onOrgChanged)
  }, [user])

  const value: ProjectContextType = {
    selectedProjectId,
    defaultProjectId,
    setSelectedProjectId,
    setDefaultProjectId,
    projects,
    projectsLoading,
    projectsError,
    hasFetched,
    refetchProjects,
    removeProjectFromList,
  }

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}
